import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Clock, ChevronRight, List, Grid, Search, X } from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import Breadcrumbs from '../components/Breadcrumbs';
import { calculateReadingTime } from '../data/educationCategories';
import { getAllAnatomyPages } from '../data/anatomyPages';
import { topicData } from './EducationalTopicPage';
import { commonConcernsArticles } from '../data/commonConcernsContent';
import { urologicCancerArticles } from '../data/urologicCancerContent';
import { standaloneArticles } from '../data/standaloneArticles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EducationLibrary = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Categories for filtering
  const categories = [
    { id: 'all', label: 'All Topics', icon: '📚' },
    { id: 'anatomy', label: 'Anatomy', icon: '🔬' },
    { id: 'cancer', label: 'Cancer', icon: '🎗️' },
    { id: 'concern', label: 'Common Concerns', icon: '❓' },
    { id: 'guide', label: 'Patient Guides', icon: '📘' }
  ];

  // Load articles from API
  useEffect(() => {
    const loadContent = async () => {
      try {
        const response = await axios.get(`${API}/blog?limit=100`);
        setArticles(response.data);
      } catch (error) {
        console.error("Error loading articles:", error);
      } finally {
        setLoading(false);
      }
    };
    loadContent();
  }, []);

  // Build all content
  const allContent = useMemo(() => {
    const content = [];

    // Add blog articles
    articles.forEach(article => {
      const readingTime = calculateReadingTime(article.content);
      content.push({
        id: article.id,
        title: article.title,
        type: 'article',
        path: `/learn/${article.id}`,
        category: article.category,
        readingTime,
        description: article.content?.substring(0, 120) + '...',
        searchText: `${article.title} ${article.content || ''} ${article.category || ''}`.toLowerCase()
      });
    });

    // Add anatomy pages
    getAllAnatomyPages().forEach(page => {
      const contentText = page.simpleExplanation + ' ' + page.moreDetail;
      const readingTime = calculateReadingTime(contentText);
      content.push({
        id: page.id,
        title: page.title,
        type: 'anatomy',
        path: `/anatomy/${page.id}`,
        category: 'Anatomy',
        readingTime,
        description: page.subtitle,
        searchText: `${page.title} ${page.subtitle} ${page.simpleExplanation} ${page.moreDetail} anatomy`.toLowerCase()
      });
    });

    // Add educational topics (Pelvic PT, BCG, Catheter, Nephrostomy)
    Object.entries(topicData).forEach(([id, topic]) => {
      content.push({
        id: id,
        title: topic.title,
        type: 'guide',
        path: `/education/${id}`,
        category: topic.category,
        readingTime: topic.readingTime,
        description: topic.description,
        icon: topic.icon,
        searchText: `${topic.title} ${topic.description} ${topic.category} guide`.toLowerCase()
      });
    });

    // Add Common Concerns articles
    commonConcernsArticles.forEach(article => {
      const readingTime = Math.ceil(article.content.en.split(' ').length / 200);
      const tags = article.tags?.join(' ') || '';
      content.push({
        id: article.id,
        title: article.title.en,
        type: 'concern',
        path: `/common-concerns/${article.id}`,
        category: 'Common Concerns',
        readingTime,
        description: article.content.en.substring(0, 120) + '...',
        searchText: `${article.title.en} ${article.content.en} ${tags} concern`.toLowerCase()
      });
    });

    // Add Urologic Cancer articles
    urologicCancerArticles.forEach(article => {
      const readingTime = Math.ceil(article.content.en.split(' ').length / 200);
      const tags = article.tags?.join(' ') || '';
      content.push({
        id: article.id,
        title: article.title.en,
        type: 'cancer',
        path: `/cancer/${article.id}`,
        category: 'Cancer',
        readingTime,
        description: article.content.en.substring(0, 120) + '...',
        searchText: `${article.title.en} ${article.content.en} ${tags} cancer`.toLowerCase()
      });
    });

    // Add Standalone articles
    standaloneArticles.forEach(article => {
      const readingTime = Math.ceil(article.content.en.split(' ').length / 200);
      const tags = article.tags?.join(' ') || '';
      content.push({
        id: article.id,
        title: article.title.en,
        type: 'article',
        path: `/articles/${article.id}`,
        category: 'Article',
        readingTime,
        description: article.content.en.substring(0, 120) + '...',
        searchText: `${article.title.en} ${article.content.en} ${tags}`.toLowerCase()
      });
    });

    // Add special pages
    content.push({
      id: 'urologic-instruments',
      title: 'Urologic Instruments – A Patient\'s Guide',
      type: 'guide',
      path: '/education/urologic-instruments',
      category: 'Education',
      readingTime: 8,
      description: 'Visual guide to urologic instruments: cystoscopes, ureteroscopes, stents, catheters, and more...',
      searchText: 'urologic instruments cystoscope ureteroscope stent catheter guide tools devices'.toLowerCase()
    });

    return content;
  }, [articles]);

  // Filter content based on search and category
  const filteredContent = useMemo(() => {
    let result = [...allContent];

    // Filter by category
    if (selectedCategory !== 'all') {
      result = result.filter(item => item.type === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => item.searchText.includes(query));
    }

    // Sort alphabetically
    result.sort((a, b) => a.title.localeCompare(b.title));

    return result;
  }, [allContent, selectedCategory, searchQuery]);

  // Group by first letter for A-Z view
  const groupedByLetter = useMemo(() => {
    if (viewMode !== 'az') return null;
    
    const groups = {};
    filteredContent.forEach(item => {
      const firstLetter = item.title.charAt(0).toUpperCase();
      if (!groups[firstLetter]) groups[firstLetter] = [];
      groups[firstLetter].push(item);
    });
    return groups;
  }, [filteredContent, viewMode]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'article': return '📄';
      case 'anatomy': return '🔬';
      case 'guide': return '📘';
      case 'concern': return '❓';
      case 'cancer': return '🎗️';
      default: return '📖';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'article': return 'Article';
      case 'anatomy': return 'Anatomy';
      case 'guide': return 'Patient Guide';
      case 'concern': return 'Common Concern';
      case 'cancer': return 'Cancer Info';
      default: return 'Topic';
    }
  };

  if (loading) {
    return (
      <PageWrapper>
        <div className="loading-state" data-testid="loading-state">
          <div className="spinner"></div>
          <p>Loading educational content...</p>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Breadcrumbs items={[{ label: 'Education Library', path: '/education' }]} />

      {/* Header */}
      <div className="education-library-header" data-testid="education-header">
        <div className="education-library-title">
          <BookOpen size={28} />
          <div>
            <h1>Education Library</h1>
            <p>Trusted urology information by topic, symptom, or procedure</p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="education-search-section">
        <div className="education-search-wrapper">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search topics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="education-search-input"
            data-testid="education-search-input"
          />
          {searchQuery && (
            <button 
              className="education-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Category Tabs */}
      <div className="education-category-tabs" data-testid="category-tabs">
        {categories.map(cat => (
          <button 
            key={cat.id}
            className={`category-tab ${selectedCategory === cat.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat.id)}
            data-testid={`tab-${cat.id}`}
          >
            <span className="tab-icon">{cat.icon}</span>
            <span className="tab-label">{cat.label}</span>
          </button>
        ))}
      </div>

      {/* Results Count & View Toggle */}
      <div className="education-toolbar" data-testid="education-toolbar">
        <div className="education-results-count" data-testid="results-count">
          {filteredContent.length} topic{filteredContent.length !== 1 ? 's' : ''}
          {searchQuery && ` for "${searchQuery}"`}
        </div>
        
        <div className="view-toggle" data-testid="view-toggle">
          <button 
            className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid view"
          >
            <Grid size={18} />
          </button>
          <button 
            className={`view-btn ${viewMode === 'az' ? 'active' : ''}`}
            onClick={() => setViewMode('az')}
            title="A-Z list"
          >
            <List size={18} />
            <span>A-Z</span>
          </button>
        </div>
      </div>

      {/* Content Display */}
      {viewMode === 'az' && groupedByLetter ? (
        <div className="education-az-view" data-testid="education-az">
          {Object.keys(groupedByLetter).sort().map(letter => (
            <div key={letter} className="az-group">
              <h3 className="az-letter">{letter}</h3>
              <div className="az-items">
                {groupedByLetter[letter].map(item => (
                  <Link
                    key={`${item.type}-${item.id}`}
                    to={item.path}
                    className="az-item"
                  >
                    <span className="az-item-icon">{getTypeIcon(item.type)}</span>
                    <span className="az-item-title">{item.title}</span>
                    <span className="az-item-type">{getTypeLabel(item.type)}</span>
                    <ChevronRight size={16} className="az-item-arrow" />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="education-grid" data-testid="education-grid">
          {filteredContent.length > 0 ? (
            filteredContent.map(item => (
              <Link
                key={`${item.type}-${item.id}`}
                to={item.path}
                className="education-card"
                data-testid={`education-card-${item.id}`}
              >
                <div className="education-card-header">
                  <span className="education-card-icon">{getTypeIcon(item.type)}</span>
                  <span className="education-card-type">{getTypeLabel(item.type)}</span>
                </div>
                <h3 className="education-card-title">{item.title}</h3>
                <div className="education-card-footer">
                  <span className="education-card-time">
                    <Clock size={14} /> {item.readingTime} min read
                  </span>
                  <ChevronRight size={16} className="education-card-arrow" />
                </div>
              </Link>
            ))
          ) : (
            <div className="empty-state" data-testid="empty-state">
              <BookOpen size={48} />
              <h3>No topics found</h3>
              <p>Try a different search or category</p>
              <button 
                className="btn-primary" 
                onClick={() => { setSelectedCategory('all'); setSearchQuery(''); }}
              >
                View All Topics
              </button>
            </div>
          )}
        </div>
      )}

      {/* Disclaimer */}
      <div className="education-disclaimer" data-testid="education-disclaimer">
        <p>Educational information only — not medical advice. Always consult your healthcare provider.</p>
      </div>
    </PageWrapper>
  );
};

export default EducationLibrary;
